import { useState } from 'react'
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { LineChart } from '@mui/x-charts/LineChart'

import { api } from '../../lib/api'

type RangeKey = '1h' | '24h' | '7d'
const RANGE_HOURS: Record<RangeKey, number> = { '1h': 1, '24h': 24, '7d': 24 * 7 }

function formatBytes(bytes: number | null) {
  if (bytes === null) return '—'
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  return `${(bytes / 1024 ** i).toFixed(1)} ${units[i]}`
}

function formatDuration(seconds: number) {
  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3_600)
  const minutes = Math.floor((seconds % 3_600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

export function SystemHealthTab() {
  const [range, setRange] = useState<RangeKey>('24h')

  const live = useQuery({
    queryKey: ['system-health', 'live'],
    queryFn: () => api.systemHealth.getLive(),
    refetchInterval: 15_000,
  })

  const history = useQuery({
    queryKey: ['system-health', 'history', range],
    queryFn: () => api.systemHealth.getHistory(RANGE_HOURS[range]),
    refetchInterval: 60_000,
  })

  const stats = live.data
  const snapshots = history.data ?? []

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
            System health
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Live platform stats and load history — refreshes automatically.
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <StatTile label="Active users" value={stats ? String(stats.active_ws_users) : '—'}
          hint="Connected right now (live sockets)" />
        <StatTile label="Logged in today" value={stats ? String(stats.logged_in_today) : '—'} />
        <StatTile label="DB connections" value={stats ? `${stats.db_pool_total - stats.db_pool_idle} / ${stats.db_pool_total}` : '—'}
          hint="In use / total, across all org pools" />
        <StatTile label="Disk free" value={stats ? formatBytes(stats.disk_free_bytes) : '—'}
          hint={stats ? `of ${formatBytes(stats.disk_total_bytes)} total` : undefined} />
        <StatTile label="Since last restart" value={stats ? formatDuration(stats.uptime_seconds) : '—'}
          hint="Not a historical uptime % — see below" />
      </Stack>

      <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 3 }}>
        "Since last restart" reflects how long this process has been running, not overall
        availability — a crashed process can't report time it was down. Tracking real uptime %
        needs an external check hitting this server from outside it.
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Load history</Typography>
        <Tabs value={range} onChange={(_, v) => setRange(v)} sx={{ minHeight: 32 }}>
          <Tab value="1h" label="1h" sx={{ minHeight: 32, py: 0.5 }} />
          <Tab value="24h" label="24h" sx={{ minHeight: 32, py: 0.5 }} />
          <Tab value="7d" label="7d" sx={{ minHeight: 32, py: 0.5 }} />
        </Tabs>
      </Stack>

      <Stack spacing={3}>
        <ChartCard title="Requests / errors per minute" empty={snapshots.length === 0}>
          <LineChart
            series={[
              { data: snapshots.map((s) => s.request_count), label: 'Requests', color: '#0D9488', showMark: false, curve: 'monotoneX' },
              { data: snapshots.map((s) => s.error_count), label: 'Errors (5xx)', color: '#DC2626', showMark: false, curve: 'monotoneX' },
            ]}
            xAxis={[{ scaleType: 'point', data: snapshots.map((s) => formatTick(s.captured_at, range)) }]}
            height={220}
            margin={{ top: 16, right: 16, bottom: 24, left: 32 }}
          />
        </ChartCard>

        <ChartCard title="Response time (ms)" empty={snapshots.every((s) => s.avg_response_ms === null)}>
          <LineChart
            series={[
              { data: snapshots.map((s) => s.avg_response_ms), label: 'Avg', color: '#2563EB', showMark: false, curve: 'monotoneX', connectNulls: true },
              { data: snapshots.map((s) => s.p95_response_ms), label: 'p95', color: '#D97706', showMark: false, curve: 'monotoneX', connectNulls: true },
            ]}
            xAxis={[{ scaleType: 'point', data: snapshots.map((s) => formatTick(s.captured_at, range)) }]}
            height={220}
            margin={{ top: 16, right: 16, bottom: 24, left: 32 }}
          />
        </ChartCard>

        <ChartCard title="DB connections in use" empty={snapshots.length === 0}>
          <LineChart
            series={[
              { data: snapshots.map((s) => s.db_pool_total - s.db_pool_idle), label: 'In use', color: '#7C3AED', showMark: false, curve: 'monotoneX', area: true },
            ]}
            xAxis={[{ scaleType: 'point', data: snapshots.map((s) => formatTick(s.captured_at, range)) }]}
            height={220}
            margin={{ top: 16, right: 16, bottom: 24, left: 32 }}
          />
        </ChartCard>
      </Stack>
    </Box>
  )
}

function formatTick(iso: string, range: RangeKey) {
  const d = new Date(iso)
  return range === '7d'
    ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 160,
      p: 2, borderRadius: 2,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
    }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 22, fontWeight: 700, lineHeight: 1.2,
        letterSpacing: '-0.02em', mt: 0.5,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>{hint}</Typography>
      )}
    </Box>
  )
}

function ChartCard({ title, empty, children }: { title: string; empty: boolean; children: React.ReactNode }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>{title}</Typography>
      {empty ? (
        <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>No data yet — the first snapshot lands within a minute of server start.</Typography>
        </Box>
      ) : children}
    </Box>
  )
}
